import { useInstance } from '@meraki-internal/react-dependency-injection';
import { FC, useEffect } from 'react';
import { NewCareMapWizard } from '../../wizard/mini-wizard/wizards/NewCareMapWizard';
import { NudgeBadge } from '../../components/NudgeBadge';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { ShareNoteState } from './ShareNoteState';
import { ToastPresenter } from '../../ToastBinder';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';

export type IShareNoteNudgeProps = {
    note: ICaremapNoteResponse;
};

export const ShareNoteNudge: FC<IShareNoteNudgeProps> = ({ note }) => {
    const history = useInstance(HistoryViewModel);
    const newCareMapMiniWizard = useInstance(NewCareMapWizard);
    const toastPresenter = useInstance(ToastPresenter);
    const tracking = useInstance(MixPanelEventEmitter);
    const notesFactory = useInstance(CaremapNotesModelFactory);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const shareNoteState = useInstance(ShareNoteState);
    useSubscription(() => shareNoteState);

    const careMap = careMapState.getActiveCaremap();
    const isViewer = careMap && !careMap?.links?.self?.actions.includes('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    useEffect(() => {
        shareNoteState.setState({ note });
    }, [note]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note.isPrivate || isViewer || isAnonymous) {
        return null;
    }

    const hasShared = shareNoteState.hasShared();
    const onSetupSharing = () => {
        if (!hasShared) {
            tracking.track('Share Set Up Initiated');
        }

        const encodedParams = encodeURIComponent(`{"mode":"share-note","noteId":"${note.noteId}"}`);
        const shareNoteRoute = `${history.getCurrentLocation().pathname}?drawer=${encodedParams}`;

        // fill out caremap info
        if (careMapState.getIncompleteCareMap()) {
            newCareMapMiniWizard.start({
                'skip-consent': true,
                'advance-to': shareNoteRoute
            });
        } else {
            // caremap info filled out, if shared already save public and go to community
            // otherwise to to the share with contacts view
            if (hasShared) {
                shareNoteState.makeNotePublic();
                history.push('/community-redirect');

                toastPresenter.toast({
                    duration: 3000,
                    message: 'Community post added!'
                });

                tracking.track('Note Posted to Community', () => (
                    { 'Method': 'Share Banner', 'Is Pinned': notesFactory.notePinsState.isPinned(note.noteId) }
                ));
            } else {
                history.push(shareNoteRoute);
            }
        }
    };

    const content = hasShared ?
        'Share this note with your Innerhive community!' :
        'Set up community sharing to keep family, friends, and care teams in the loop!';
    const btnLabel = hasShared ? 'Post to community' : 'Set up sharing';

    return (
        <NudgeBadge
            excludeIcon
            message={content}
            buttons={[{
                label: btnLabel,
                'data-type': 'setup-sharing',
                clickHandler: onSetupSharing
            }]}
            dismissableStorageId={`share-note-${note.noteId}`}
        />
    );
};
